import { useState } from "react";
import { changePassword } from "../api";
import type { UserSession } from "../types";

interface ProfileViewProps {
  user: any;
  onUserUpdate?: (updatedUser: UserSession) => void;
}

const ProfileView = ({ user, onUserUpdate }: ProfileViewProps) => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("Nowe hasła nie są identyczne.");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError("Hasło musi mieć co najmniej 8 znaków.");
      return;
    }

    setLoading(true);

    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      setSuccess("Hasło zostało zmienione pomyślnie.");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="content-section profile">
      <header>
        <h2>Profil użytkownika</h2>
        <p>Zarządzaj danymi swojego konta i bezpieczeństwem.</p>
      </header>

      <div className="profile-grid">
        {/* Contractor Information */}
        <article className="profile-card">
          <h3>Dane kontrahenta</h3>
          {user?.contractor ? (
            <div className="profile-info">
              <div className="info-item">
                <label>Nazwa:</label>
                <p>{user.contractor?.name || "-"}</p>
              </div>
              {user.contractor?.address && (
                <div className="info-item">
                  <label>Adres:</label>
                  <p>{user.contractor?.address}</p>
                </div>
              )}
              {user.contractor?.city && (
                <div className="info-item">
                  <label>Miasto:</label>
                  <p>{user.contractor?.city}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="info-empty">
              Brak dostępnych informacji o kontrahencie.
            </p>
          )}
        </article>

        {/* User Information */}
        <article className="profile-card">
          <h3>Dane osobowe</h3>
          <div className="profile-info">
            <div className="info-item">
              <label>Imię:</label>
              <p>{user?.firstName || "-"}</p>
            </div>
            <div className="info-item">
              <label>Nazwisko:</label>
              <p>{user?.lastName || "-"}</p>
            </div>
            <div className="info-item">
              <label>E-mail:</label>
              <p>{user?.email || "-"}</p>
            </div>
            {user?.phone && (
              <div className="info-item">
                <label>Telefon:</label>
                <p>{user?.phone}</p>
              </div>
            )}
          </div>
        </article>

        {/* Change Password Form */}
        <article className="profile-card profile-card--full">
          <h3>Zmiana hasła</h3>
          <form onSubmit={handlePasswordSubmit} className="profile-form">
            <label htmlFor="current-password">
              <span>Aktualne hasło</span>
              <input
                id="current-password"
                type="password"
                name="currentPassword"
                autoComplete="current-password"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                required
              />
            </label>
            <label htmlFor="new-password">
              <span>Nowe hasło</span>
              <input
                id="new-password"
                type="password"
                name="newPassword"
                autoComplete="new-password"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
                minLength={8}
              />
            </label>
            <label htmlFor="confirm-new-password">
              <span>Potwierdź nowe hasło</span>
              <input
                id="confirm-new-password"
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                required
                minLength={8}
              />
            </label>
            <p className="form-hint">
              Hasło musi mieć co najmniej 8 znaków, zawierać wielkie i małe
              litery oraz cyfrę.
            </p>
            {error && <div className="alert">{error}</div>}
            {success && <div className="alert success">{success}</div>}
            <button type="submit" disabled={loading} className="primary">
              {loading ? "Zapisywanie..." : "Zmień hasło"}
            </button>
          </form>
        </article>
      </div>
    </section>
  );
};

export default ProfileView;
